/**
 * Keep antd popups alive in a browser whose animation frames have stopped.
 *
 * Chromium pauses `requestAnimationFrame` when it believes the window is hidden or occluded, and its
 * Windows occlusion tracker gets that wrong inside a Remote Desktop session. antd popups (Select,
 * DatePicker, Modal, Drawer, Tooltip) step through rc-motion on rAF BEFORE their fade even starts,
 * so a starved frame loop leaves every one of them in the DOM at `opacity: 0` — "the dropdown does
 * not open" while the static page keeps working, because React commits do not use rAF.
 *
 * A callback the browser has not run within STALL_MS runs from a timer instead; whichever fires
 * first wins and the other becomes a no-op. A browser that is ticking clears the timer on every
 * frame and pays nothing. Installed once from `src/index.js`; `@rc-component/util`'s `raf` reads
 * the global at call time, so patch order does not matter.
 *
 * ponytail: a global shim — swap for a per-library hook if antd ever exposes one.
 */
export const STALL_MS = 50;

export function installRafWatchdog(win = window) {
  const nativeRaf = win.requestAnimationFrame.bind(win);
  const nativeCaf = win.cancelAnimationFrame.bind(win);
  const timers = new Map(); // frame id -> timeout id

  win.requestAnimationFrame = cb => {
    let done = false;
    let timer;
    const run = ts => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      timers.delete(id);
      cb(ts);
    };
    const id = nativeRaf(run);
    timer = setTimeout(() => run(win.performance.now()), STALL_MS);
    timers.set(id, timer);
    return id;
  };

  win.cancelAnimationFrame = id => {
    clearTimeout(timers.get(id));
    timers.delete(id);
    nativeCaf(id);
  };
}
